import { type Lang, toggleLang, useLang } from '../lang';

// The site's own words. Kept here rather than in the old translation files so the redesign can drop them later.
const EN = {
  board: 'Board',
  schedule: 'Schedule',
  materials: 'Materials',
  practice: 'Practice',
  play: 'Slime',
  class: 'Class',
  loilo: 'Hand in on Loilo Note',
  loiloShort: 'Loilo Note',
  language: '日本語',
  languageLabel: 'Switch to Japanese',
  privacy: 'Privacy',
  today: 'Today',
  nextUp: 'Next up',
  noLessonToday: 'No lesson today.',
  thisWeek: 'This week',
  nothingThisWeek: 'Nothing else this week.',
  newMaterials: 'New on the board',
  openSchedule: 'See the full schedule',
  openMaterials: 'Open materials',
  handIn: 'Hand in',
  forGroup: 'Only',
  liveGame: 'Class game',
  liveGameBody: 'When the teacher starts a game, join it here with the code on the screen.',
  joinGame: 'Join a game',
  done: 'Done',
  upcoming: 'Coming up',
  earlier: 'Earlier',
  noLink: 'Shown in class',
  noMaterials: 'Coming soon.',
  meteorSolo: 'Meteor Defense',
  meteorSoloBlurb: 'Put phrases in order to save the town.',
  playSolo: 'Play',
  playIntro: 'Type the code on the teacher’s screen.',
  code: 'Code',
  yourSlime: 'Your slime',
  makeSlime: 'Make your slime',
  makeSlimeHint: 'Make your slime once, before your first game. It gets a fun name, not your real one.',
  changeSlime: 'Change it',
  slimeHint: 'Your slime joins the game with you.',
  join: 'Join',
  checking: 'Checking…',
  notFound: 'No game has that code. Check the screen and try again.',
  unreachable: 'Could not reach the game. Check the Wi-Fi and try again.',
  rejoin: 'Go back to your game',
  myRoom: 'Your room',
  noSlimeHere: 'No slime on this device yet',
  noSlimeIntro: 'Made one on another laptop or phone? Bring it back with your player code or sign-in. New here? Make one. It gets a fun name, not your real one.',
  bringBack: 'Bring my slime back',
  makeNew: 'Make a new slime',
  goHome: 'Go to your room',
  joinClass: 'Join the class',
  addClass: 'Add another class',
  loading: 'Opening your class board…',
  offlineNoBoard: 'Can’t reach the class board. Check the Wi-Fi and try again.',
  offlineNote: 'Offline: showing the board as it was last time.',
  codeLabel: 'Class code',
  codeIntro: 'Type the code your teacher gave your class.',
  lostCode: 'Lost the code? Ask your teacher at the start of class.',
  wrongCode: 'That code is not right. Check it and try again.',
  waitCode: 'Too many wrong codes. Wait ten minutes and try again.',
  inviteExpired: 'This invite link has expired. Ask for a new one.',
  inviteWrong: 'This invite link doesn’t work. Check that the whole link was copied.',
  continue: 'Continue',
  addIntro: 'Type the code for another class you take.',
  manageClasses: 'Add or remove a class',
  quiz: 'QUIZ',
  quizIn: 'Quiz',
  nextQuiz: 'Next quiz',
  yourClasses: 'Your classes',
  remove: 'Remove',
  removeConfirm: 'Remove {name} from this laptop? You will need the class code to add it again.',
  addClassShort: '+ Class',
  weekPaint: 'THIS WEEK',
  thisWeekCaps: 'THIS WEEK',
  nextWeekPaint: 'NEXT WEEK',
  quizPaint: 'NEXT QUIZ',
  newPaint: 'NEW',
  fullSchedule: 'Full schedule →',
  inDays: (n: number) => (n === 0 ? 'today' : n === 1 ? 'tomorrow' : `in ${n} days`),
  toFinals: (n: number) => (n === 0 ? 'Finals today' : n === 1 ? 'Finals tomorrow' : n < 14 ? `${n} days to finals` : `${Math.round(n / 7)} weeks to finals`),
  handInToday: 'HAND IN TODAY',
  handInOn: (day: string) => `HAND IN · ${day}`,
  openLoilo: 'Open Loilo Note ↗',
  joinClassGame: 'Join a class game',
  liveGameHint: 'When your teacher opens one, a note goes up here.',
  practiceIntro: 'On your own, any time. Laptop or phone.',
  meteorTag: 'SOLO GAME · WORD ORDER',
  moreGames: 'More games go up here as they are made.',
  showEarlier: 'Show earlier weeks',
  hideEarlier: 'Hide earlier weeks',
  nothingYet: 'Nothing written up yet.',
  dayHeads: ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'],
  holidayMark: '祝',
  dagashiyaOpen: 'Your class dagashiya is open',
  teacherOpened: 'your teacher opened it',
  rejoinShort: 'Go back in',
  konpeito: (n: number) => `${n} konpeito`,
  capsules: (n: number) => (n === 1 ? '1 capsule to open' : `${n} capsules to open`),
  changeSlimeLong: 'Change your slime',
  gotCode: 'Got a code?',
  noGameShort: 'No class game now',
  joinAfter: 'Make your slime to join',
  forTeachers: 'For teachers',
};

const JA: typeof EN = {
  board: 'ボード',
  schedule: '予定',
  materials: '教材',
  practice: '練習',
  play: 'スライム',
  class: 'クラス',
  loilo: 'ロイロノートで提出',
  loiloShort: 'ロイロノート',
  language: 'English',
  languageLabel: '英語に切り替える',
  privacy: 'プライバシー',
  today: '今日',
  nextUp: '次の授業',
  noLessonToday: '今日は授業がありません。',
  thisWeek: '今週',
  nothingThisWeek: '今週はほかに予定がありません。',
  newMaterials: '新しい教材',
  openSchedule: '予定をすべて見る',
  openMaterials: '教材を開く',
  handIn: '提出',
  forGroup: '対象',
  liveGame: 'クラスゲーム',
  liveGameBody: '先生がゲームを始めたら、画面のコードを入力して参加します。',
  joinGame: 'ゲームに参加',
  done: '終了',
  upcoming: 'これから',
  earlier: 'これまで',
  noLink: '授業で配布',
  noMaterials: '近日公開。',
  meteorSolo: 'メテオ・ディフェンス',
  meteorSoloBlurb: 'フレーズを並べて町を守ろう。',
  playSolo: 'あそぶ',
  playIntro: '先生の画面のコードを入力してください。',
  code: 'コード',
  yourSlime: 'あなたのスライム',
  makeSlime: 'スライムを作る',
  makeSlimeHint: '最初のゲームの前に一度だけ作ります。名前は本名ではなく、楽しい名前がつきます。',
  changeSlime: '変える',
  slimeHint: 'スライムが一緒にゲームに参加します。',
  join: '参加する',
  checking: '確認中…',
  notFound: 'そのコードのゲームはありません。画面を確認してもう一度入力してください。',
  unreachable: 'ゲームにつながりません。Wi-Fiを確認してもう一度試してください。',
  rejoin: 'ゲームに戻る',
  myRoom: 'じぶんの部屋',
  noSlimeHere: 'この端末にはまだスライムがいません',
  noSlimeIntro: 'ほかのパソコンやスマホで作った？プレイヤーコードかサインインで呼び戻せます。はじめて？新しく作ろう。名前は本名ではなく、楽しい名前がつきます。',
  bringBack: 'スライムを呼び戻す',
  makeNew: '新しく作る',
  goHome: '部屋へ行く',
  joinClass: 'クラスに参加する',
  addClass: 'ほかのクラスを追加',
  loading: 'クラスボードを開いています…',
  offlineNoBoard: 'クラスボードにつながりません。Wi-Fiを確認してもう一度試してください。',
  offlineNote: 'オフライン：前回のボードを表示しています。',
  codeLabel: 'クラスコード',
  codeIntro: '先生から配られたクラスコードを入力してください。',
  lostCode: 'コードを忘れたら、授業のはじめに先生に聞いてください。',
  wrongCode: 'クラスコードが正しくありません。確認してもう一度入力してください。',
  waitCode: 'まちがいが多すぎます。10分待ってからもう一度試してください。',
  inviteExpired: 'この招待リンクは期限切れです。新しいリンクをもらってください。',
  inviteWrong: '招待リンクが正しくありません。リンク全体をコピーしたか確認してください。',
  continue: '続ける',
  addIntro: 'ほかに受けているクラスのコードを入力してください。',
  manageClasses: 'クラスの追加・削除',
  quiz: '小テスト',
  quizIn: '小テスト',
  nextQuiz: '次の小テスト',
  yourClasses: '登録中のクラス',
  remove: '削除',
  removeConfirm: '{name}をこのパソコンから削除しますか？もう一度追加するにはクラスコードが必要です。',
  addClassShort: '+ クラス',
  weekPaint: '予定 · 今週',
  thisWeekCaps: '今週',
  nextWeekPaint: '予定 · 来週',
  quizPaint: '次の小テスト',
  newPaint: '新しい教材',
  fullSchedule: '予定をすべて見る →',
  inDays: (n: number) => (n === 0 ? '今日' : n === 1 ? '明日' : `あと${n}日`),
  toFinals: (n: number) => (n === 0 ? '今日から期末' : n === 1 ? '明日から期末' : n < 14 ? `期末まであと${n}日` : `期末まであと${Math.round(n / 7)}週`),
  handInToday: '今日提出',
  handInOn: (day: string) => `提出 · ${day}`,
  openLoilo: 'ロイロノートを開く ↗',
  joinClassGame: 'クラスゲームに参加',
  liveGameHint: '先生がゲームを開くと、ここにお知らせが出ます。',
  practiceIntro: 'ひとりで、いつでも。パソコンでもスマホでも。',
  meteorTag: 'ひとりゲーム · 語順',
  moreGames: 'ゲームができたら、ここに貼っていきます。',
  showEarlier: 'これまでの週を見る',
  hideEarlier: 'これまでの週をかくす',
  nothingYet: 'まだ何も書かれていません。',
  dayHeads: ['日', '月', '火', '水', '木', '金', '土'],
  holidayMark: '祝',
  dagashiyaOpen: 'クラスの駄菓子屋が開いています',
  teacherOpened: '先生が開きました',
  rejoinShort: 'ゲームに戻る',
  konpeito: (n: number) => `こんぺいとう ${n}こ`,
  capsules: (n: number) => `開けられるカプセル ${n}こ`,
  changeSlimeLong: 'スライムを変える',
  gotCode: 'コードがある？',
  noGameShort: 'いまはクラスのゲームはありません',
  joinAfter: 'スライムを作ると参加できます',
  forTeachers: '先生用',
};

export type { Lang };

// The site's words in the language in use (lang.ts): one language at a time, switched from the rail's button.
export function useStrings() {
  const lang = useLang();
  return { s: lang === 'en' ? EN : JA, lang, toggle: toggleLang };
}
